import { useState, FormEvent, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { AuthBrand } from '../components/AuthBrand';
import { buildFederatedQuery } from '../utils/federatedAuth';
import { authApi } from '../services/api';
import { isStaffEmail, STAFF_EMAIL_SUFFIX } from '../lib/platforms';
import { Eye, EyeOff, Loader2, Check, X } from 'lucide-react';

export function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signup, error } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [staffOnly, setStaffOnly] = useState(false);

  const clientId = searchParams.get('client_id');

  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
  };
  const isPasswordValid = Object.values(passwordChecks).every(Boolean);

  const federatedParams = buildFederatedQuery({
    client_id: searchParams.get('client_id'),
    state: searchParams.get('state'),
    code_challenge: searchParams.get('code_challenge'),
    redirect_uri: searchParams.get('redirect_uri'),
  });
  const loginPath = federatedParams ? `/auth/federated-login${federatedParams}` : '/login';

  useEffect(() => {
    if (!clientId) return;
    authApi
      .getApplication(clientId)
      .then((app) => setStaffOnly(app.staffOnly))
      .catch(() => setStaffOnly(false));
  }, [clientId]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    const normalizedEmail = email.trim().toLowerCase();
    if (staffOnly && !isStaffEmail(normalizedEmail)) {
      setLocalError(`This application is restricted to ${STAFF_EMAIL_SUFFIX} staff accounts.`);
      return;
    }

    if (!isPasswordValid) {
      setLocalError('Please meet all password requirements');
      return;
    }
    setIsLoading(true);
    try {
      const result = await signup({ email: normalizedEmail, password, firstName, lastName });
      if (result.requiresActivation) {
        const activateParams = new URLSearchParams({ email: result.email });
        if (federatedParams) {
          const fed = new URLSearchParams(federatedParams.slice(1));
          fed.forEach((v, k) => activateParams.set(k, v));
        }
        navigate(`/activate?${activateParams.toString()}`);
        return;
      }
      navigate(loginPath);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Signup failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="relative w-full max-w-md animate-fade-in">
        <AuthBrand />
        <div className="bg-background-secondary border border-border rounded-xl p-8 shadow-xl">
          <div className="text-center mb-8">
            <h1 className="text-xl font-semibold text-foreground mb-2">Create your account</h1>
            <p className="text-sm text-foreground-secondary">Central identity for all Tyaus services</p>
          </div>

          {(localError || error) && (
            <div className="mb-6 p-3 bg-error/10 border border-error/20 rounded-lg text-sm text-error">
              {localError || error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="label">First name</label>
                <input id="firstName" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="input" placeholder="John" />
              </div>
              <div>
                <label htmlFor="lastName" className="label">Last name</label>
                <input id="lastName" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} className="input" placeholder="Doe" />
              </div>
            </div>
            <div>
              <label htmlFor="email" className="label">Email</label>
              <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input" placeholder="you@example.com" required />
            </div>
            <div>
              <label htmlFor="password" className="label">Password</label>
              <div className="relative">
                <input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} className="input pr-10" required />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-muted">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {password && (
                <div className="mt-3 space-y-1.5">
                  {[
                    { key: 'length', label: 'At least 8 characters' },
                    { key: 'uppercase', label: 'One uppercase letter' },
                    { key: 'lowercase', label: 'One lowercase letter' },
                    { key: 'number', label: 'One number' },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-2 text-xs">
                      {passwordChecks[key as keyof typeof passwordChecks] ? (
                        <Check className="w-3.5 h-3.5 text-success" />
                      ) : (
                        <X className="w-3.5 h-3.5 text-foreground-muted" />
                      )}
                      <span className={passwordChecks[key as keyof typeof passwordChecks] ? 'text-success' : 'text-foreground-muted'}>{label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button type="submit" disabled={isLoading || !isPasswordValid} className="btn-primary w-full py-2.5">
              {isLoading ? <><Loader2 className="w-4 h-4 animate-spin" />Creating account...</> : 'Create account'}
            </button>
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="text-foreground-secondary">Already have an account? </span>
            <Link to={loginPath} className="text-accent hover:text-accent-hover">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
